import type {
  Attachment,
  AvailableTransition,
  BulkUpdateIssuesBody,
  BulkUpdateResult,
  ChangelogEntry,
  CreateIssueBody,
  Issue,
  IssueCard,
  TransitionIssueBody,
  TransitionIssueResponse,
  UpdateIssueBody,
} from '@tasks/contracts';

import type { Page } from '../lib/types';
import { apiRequest, apiRequestList, apiRequestResource, apiRequestVoid } from './client';

export type IssueSearchParams = {
  readonly jql?: string;
  readonly limit?: number;
  readonly offset?: number;
};

export type RankBody = {
  readonly beforeId: string | null;
  readonly afterId: string | null;
};

export function searchIssues(params: IssueSearchParams = {}, signal?: AbortSignal): Promise<Page<IssueCard>> {
  return apiRequest<Page<IssueCard>>('/issues', {
    query: { jql: params.jql, limit: params.limit, offset: params.offset },
    signal,
  });
}

export function createIssue(projectKey: string, body: CreateIssueBody): Promise<Issue> {
  return apiRequestResource<Issue>(`/projects/${encodeURIComponent(projectKey)}/issues`, 'issue', {
    method: 'POST',
    body,
  });
}

export function getIssue(key: string, signal?: AbortSignal): Promise<Issue> {
  return apiRequestResource<Issue>(`/issues/${encodeURIComponent(key)}`, 'issue', { signal });
}

export function updateIssue(key: string, body: UpdateIssueBody): Promise<Issue> {
  return apiRequestResource<Issue>(`/issues/${encodeURIComponent(key)}`, 'issue', { method: 'PATCH', body });
}

export function deleteIssue(key: string, cascade: boolean): Promise<void> {
  return apiRequestVoid(`/issues/${encodeURIComponent(key)}`, { method: 'DELETE', query: { cascade } });
}

export function getAvailableTransitions(key: string, signal?: AbortSignal): Promise<AvailableTransition[]> {
  return apiRequestList<AvailableTransition>(`/issues/${encodeURIComponent(key)}/transitions`, { signal });
}

export function transitionIssue(key: string, body: TransitionIssueBody): Promise<TransitionIssueResponse> {
  return apiRequest<TransitionIssueResponse>(`/issues/${encodeURIComponent(key)}/transitions`, {
    method: 'POST',
    body,
  });
}

/** Только `rank` — статус меняется через `/transitions` (docs/05 §6). */
export function rankIssue(key: string, body: RankBody): Promise<void> {
  return apiRequestVoid(`/issues/${encodeURIComponent(key)}/rank`, { method: 'PATCH', body });
}

export function duplicateIssue(key: string, includeSubtasks: boolean): Promise<Issue> {
  return apiRequestResource<Issue>(`/issues/${encodeURIComponent(key)}/duplicate`, 'issue', {
    method: 'POST',
    body: { includeSubtasks },
  });
}

export function listSubtasks(key: string, signal?: AbortSignal): Promise<IssueCard[]> {
  return apiRequestList<IssueCard>(`/issues/${encodeURIComponent(key)}/subtasks`, { signal });
}

export function listChangelog(
  key: string,
  params: { limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<Page<ChangelogEntry>> {
  return apiRequest<Page<ChangelogEntry>>(`/issues/${encodeURIComponent(key)}/changelog`, {
    query: { limit: params.limit, offset: params.offset },
    signal,
  });
}

/** Массовая операция — в контексте проекта (docs/05 §6, `POST /projects/:key/issues/bulk`). */
export function bulkUpdateIssues(projectKey: string, body: BulkUpdateIssuesBody): Promise<BulkUpdateResult> {
  return apiRequest<BulkUpdateResult>(`/projects/${encodeURIComponent(projectKey)}/issues/bulk`, {
    method: 'POST',
    body,
  });
}

export type { Attachment };
